import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Row, Col } from 'react-bootstrap';
import FileUpload from './FileUpload';
import Select from './Select';
import LoadingProgressBar from './LoadingProgressBar';
import Table from './Table';
import ServerError from './ServerError';
import Button from './Button';
import { templateAddAction,
         caseEditIdAction,
         templateDeleteAction,
         templateVerifyAction,
         templatesLoadAction } from '../../actions/caseActions';

import { isEmptyObject, isEmptyString } from '../../validators/formValidators';
import { getOwnerId, formatBytes, getLocalDateTimeFromIso } from '../../helpers/utils';
import { dialogHeaderFooterBgColor } from '../../helpers/colors';
import { userLogInLoadAction } from '../../actions/userActions';

class ManageDocumentTemplates extends PureComponent {
    constructor(props) {
        super(props);
        this.onError = this.onError.bind(this);
        this.onFinish = this.onFinish.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onDeleteMany = this.onDeleteMany.bind(this);
        this.loadTemplates = this.loadTemplates.bind(this);
        this.state = {
            serverError: undefined,
            loading: false,
        };
    }

    componentDidMount() {
        if (!this.props.templates && !this.props.loadingTemplates) {
            this.loadTemplates();
        }
    }

    loadTemplates() {
        this.setState({
            serverError: undefined,
            loading: true
        });

        this.props.dispatch(templatesLoadAction())
            .then((result) => {
                if (!result) {
                    this.props.dispatch(caseSetServerError('Cannot load templates attachments - networking error?'));
                    return;
                }

                if (!this.props.currentConfig) {
                    this.props.dispatch(userLogInLoadAction())
                        .then((result) => {
                            this.setState({
                                serverError: undefined,
                                loading: false
                            });
                        })
                        .catch((error) => {
                            console.error(new Error().stack, error);
                            this.setState({
                                serverError: error,
                                loading: false
                            });
                        });

                } else {
                    this.setState({
                        serverError: undefined,
                        loading: false
                    });
                }
            })
            .catch((error) => {
                console.error(new Error().stack, error);
                this.setState({
                    serverError: error,
                    loading: false
                });
            });
    }

    onSubmit(name) {
        this.setState({
            serverError: undefined,
            loading: true
        });
    }

    onError(name, errorReason) {
        this.setState({
            serverError: errorReason
        });
    }

    onFinish(name, size) {
        this.props.dispatch(templateVerifyAction('document', name))
            .then((result) => {
                if (result) {
                    this.props.dispatch(templateAddAction('document', name, size));
                    this.setState({
                        serverError: undefined,
                        loading: false,
                    });

                } else {
                    this.setState({
                        serverError: this.props.serverError,
                        loading: false,
                    });
                }
            })
            .catch((error) => {
                console.error(new Error().stack, error);
                this.setState({
                    serverError: error,
                    loading: false
                });
            });
    }

    async onDeleteMany(rows) {
        this.setState({ serverError: undefined, loading: true });
        try {
            for (const row of rows) {
                await this.props.dispatch(templateDeleteAction('document', row.data['TemplateName']));
            }

            this.setState({ loading: false });
        } catch (error) {
            console.error(new Error().stack, error);
            this.setState({ loading: false, serverError: error });
        }
    }

    onDelete(rowIndex, currentTemplate) {
        this.setState({
            serverError: undefined,
            loading: true
        });

        let templateType = 'document';
        let templateName = currentTemplate.data['TemplateName'];
        this.props.dispatch(templateDeleteAction(templateType, templateName))
            .then((result) => {
                this.setState({
                    loading: false
                });
            })
            .catch((error) => {
                console.error(new Error().stack, error);
                this.setState({
                    loading: false,
                    serverError: error
                });
            });
    }

    render() {
        const {
            open,
            cancel,
            title,
            templates,
            loggedIn,
            showAsDialog,
            filterable = true,
            showDelete = false,
        } = this.props;

        //
        // Format so the Table component loads the rows correctly.
        //

        let rows = [];
        if (!isEmptyObject(templates) && !isEmptyObject(templates['document']) && !isEmptyString('document')) {
            rows = templates['document'].map(v => {
                let row = {};
                row.data = {};
                row.data['Type'] = 'doc_templates';
                row.data['TemplateType'] = 'document';
                row.data['TemplateName'] = v.fileName;
                row.data['Name'] = v.fileName;
                row.data['$LastModified$'] = getLocalDateTimeFromIso(loggedIn, v.LastModified);
                row.data['$Size$'] = formatBytes(v.Size);
                row.data['$Placeholders$'] = v.placeholders;
                row.data['$DownloadPath$'] = `/api/v1/download/original_template/document/${v.fileName}`;
                return row;
            });
        }

        let headers = [
        {
            table: 'data',
            name: 'TemplateName',
            label: 'Name',
            filterable,
            width: '*'
        },
        {
            table: 'data',
            name: '$LastModified$',
            filterable,
            label: 'Last Modified',
            width: 120,
        }
        ];

        let loadingTemplates = typeof(this.props.templates) == 'undefined';
        let documentTemplatesComponent = (
            <FileUpload title={title}
                        loading={this.state.loading || loadingTemplates}
                        headers={headers}
                        rows={rows}
                        customFieldsMap={
                            {
                            'data.$Size$': {
                                type: 'DiskSize'
                            },
                            'data.$LastModified$': {
                                type: 'DateTimeInput'
                            },
                            }
                        }
                        folder={`${getOwnerId(this.props.loggedIn)}/template/document`}
                        extensions={['docx']}
                        sizeLimit={parseInt(process.env.AWS_TEMPLATE_SIZE_LIMIT)}
                        onError={this.onError}
                        onFinish={this.onFinish}
                        onSubmit={this.onSubmit}
                        onDelete={showDelete && this.onDelete}
                        onDeleteMany={showDelete && this.onDeleteMany}
                        downloadRow={this.props.downloadRow}
                        getTdProps={this.props.getTdProps}
                        customActionColumn={this.props.customActionColumn}
                        getRef={tableRef => {
                            if (this.props.getRef) {
                                this.props.getRef(tableRef);
                            }
                        }}
            />
            );

        if (showAsDialog) {
            documentTemplatesComponent = (
                <Modal show={open}
                    onHide={cancel}
                    backdrop='static'
                >
                    <Modal.Header closeButton style={{ backgroundColor: dialogHeaderFooterBgColor }}>
                        <Modal.Title>Configure Document Templates</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                    <Row>
                        <ServerError error={this.state.serverError} />
                    </Row>
                    <Row style={{minHeight: 450}}>
                        <Col xs={12}>
                        {documentTemplatesComponent}
                        </Col>
                    </Row>
                    </Modal.Body>
                </Modal>
            );
        }

        return documentTemplatesComponent;
    }
}

const mapStateToProps = (state) => {
    let loggedIn = state.user.loggedIn;
    return {
        templates: state.cases.templates,
        currentConfig: state.user.config,
        loggedIn,
        serverError: state.cases._serverError,
        loadingTemplates: state.cases._loadingDocumentTemplates,
    }
}

ManageDocumentTemplates.propTypes = {
    showAsDialog: PropTypes.bool,
    cancel: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    filterable: PropTypes.bool,
    showDelete: PropTypes.bool,
    customActionColumn: PropTypes.object,
};

export default connect(mapStateToProps)(ManageDocumentTemplates);
